// src/components/Counter.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Counter.css';

const Counter = ({ end, duration = 1500, suffix = '', label = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);
  const hasAnimated = useRef(false);
  const observerRef = useRef(null);

  // Animation d'incrémentation avec useCallback
  const animateValue = useCallback(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;

    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      // Easing: easeOutQuart pour fin rapide
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(easeProgress * end);
      
      setCount(current);
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    
    window.requestAnimationFrame(step);
  }, [duration, end]);

  // Observer pour déclencher l'animation au scroll
  useEffect(() => {
    const currentRef = counterRef.current; // Copie pour le cleanup
    let observer = observerRef.current;

    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          setTimeout(animateValue, 100);
        }
      });
    };

    if (currentRef && !observer) {
      observer = new IntersectionObserver(handleIntersection, { 
        threshold: 0.1 
      });
      observerRef.current = observer;
      observer.observe(currentRef);
    }

    // Cleanup function
    return () => {
      if (observer && currentRef) {
        observer.unobserve(currentRef);
      }
      if (observer) {
        observer.disconnect();
        observerRef.current = null;
      }
    };
  }, [animateValue]); // animateValue dans les dépendances

  return (
    <div className="counter-item" ref={counterRef}>
      <div className="counter-value">
        <span className="number">{isVisible ? count.toLocaleString() : '0'}</span>
        <span className="suffix">{suffix}</span>
      </div>
      <div className="counter-label">{label}</div>
    </div>
  );
};

export default Counter;